/**
 * Centralized React Query keys.
 *
 * Every key is scoped by workspaceId + portalId (and userId for per-user data)
 * so different portals never share cache entries. Keeping them in one place
 * makes invalidation predictable and avoids stringly-typed drift across hooks.
 */
export const queryKeys = {
  favorites: (workspaceId: string, portalId: string, userId: string) =>
    ["favorites", workspaceId, portalId, userId] as const,

  recentlyViewed: (workspaceId: string, portalId: string, userId: string) =>
    ["recentlyViewed", workspaceId, portalId, userId] as const,

  filters: (workspaceId: string, portalId: string, userId: string) =>
    ["filters", workspaceId, portalId, userId] as const,

  kapps: (workspaceId: string, portalId: string) =>
    ["kapps", workspaceId, portalId] as const,

  tags: (workspaceId: string, portalId: string) =>
    ["tags", workspaceId, portalId] as const,

  categories: (workspaceId: string, portalId: string, group: string) =>
    ["categories", workspaceId, portalId, group] as const,

  documentBrowse: (
    workspaceId: string,
    portalId: string,
    params: Record<string, unknown>,
  ) => ["documentBrowse", workspaceId, portalId, params] as const,

  documentSearch: (
    workspaceId: string,
    portalId: string,
    params: Record<string, unknown>,
  ) => ["documentSearch", workspaceId, portalId, params] as const,

  // The answer stream seeds this key directly (see [ESCALATION_MATCHES] in
  // routes/search), so the key must be derivable from the query + filters alone.
  escalations: (
    workspaceId: string,
    portalId: string,
    query: string,
    categories: Record<string, string[]>,
  ) => ["escalations", workspaceId, portalId, query, categories] as const,

  escalationCategories: (workspaceId: string, portalId: string) =>
    ["escalationCategories", workspaceId, portalId] as const,

  // Short-lived; never persisted (not in PERSISTED_KEY_ROOTS). The JWT is in
  // the key so a refreshed JWT (e.g. after a role change) re-mints at once.
  sasToken: (
    workspaceId: string,
    portalId: string,
    userId: string | null,
    themeLoaded: boolean,
    isPrivate: boolean,
    jwt: string | null,
  ) =>
    [
      "sasToken",
      workspaceId,
      portalId,
      userId,
      themeLoaded,
      isPrivate,
      jwt,
    ] as const,
} as const;

/**
 * Root key segments whose data is safe + useful to persist to localStorage for
 * instant boot. User-data (favorites/recent/filters) and rarely-changing
 * reference lists (kapps/tags/categories) qualify, plus the document-browse
 * landing page. Live keyword/smart-search result sets are intentionally NOT
 * persisted — they can be large and are cheap to refetch on demand.
 */
export const PERSISTED_KEY_ROOTS = new Set<string>([
  "favorites",
  "recentlyViewed",
  "filters",
  "kapps",
  "tags",
  "categories",
  "documentBrowse",
]);
